import { Component, effect, inject, OnInit, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { PiezeoService } from "../../../../app/services/piezeo.service";
import { PiezeoModalComponent } from "../piezeo-modal/piezeo-modal.component";
import { CommonModule } from "@angular/common";
import { MatTabChangeEvent, MatTabsModule } from "@angular/material/tabs";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
  FormArray,
} from "@angular/forms"; // Import Reactive Forms modules
import { MatFormFieldModule } from "@angular/material/form-field"; // For mat-form-field
import { MatSelectModule } from "@angular/material/select"; // For mat-select
import { MatInputModule } from "@angular/material/input"; // For matInput
import {
  checkSamePiezeoSerial,
  compareSameAxis,
  compareValidator,
  notEqualValidator,
} from "../../../../app/directives/form-common-validators";
import {
  MatRadioButton,
  MatRadioChange,
  MatRadioGroup,
} from "@angular/material/radio";
import { BaseUrlConstantsService } from "../../../../app/services/base-url-constants.service";
import { v4 as uuid } from "uuid";
import { PiezeoModalEditComponent } from "../piezeo-modals-edit/piezeo-modal-edit.component";

@Component({
  selector: "app-piezeo-form-edit",
  templateUrl: "./piezeo-form-edit.component.html",
  styleUrls: ["./piezeo-form-edit.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    PiezeoModalEditComponent,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule, // Add ReactiveFormsModule here
    MatFormFieldModule, // Add MatFormFieldModule
    MatSelectModule, // Add MatSelectModule
    MatInputModule, // Add MatInputModule
    MatButtonModule,
    MatRadioButton,
    MatRadioGroup,
  ],
})
export class PiezeoEditFormComponent implements OnInit {
  // url_prefix='https://qaapi.enertics.ca/piezo_service'
  // url_prefix='http://localhost:3017'
  url_prefix = `${BaseUrlConstantsService.BASE_PIEZO_SERVICE}`;
  all_sensors = [];
  subsystem_data: any = {};
  current_sensor: any = {};
  piezeoConfigForm = new FormGroup({
    no_of_axis: new FormControl(0, [Validators.required, Validators.min(1)]),
    sensors_info: new FormGroup({}),
  });

  modal_type = signal<string>("");
  total_no_of_axis = [];

  // GATEWAY CONFIGS
  current_gateway = {};
  configured_gateways = [];
  gateways_to_delete = [];

  // Rotating Asset Form
  rotating_asset_form = new FormGroup<any>({
    general_details: new FormGroup({
      asset_name: new FormControl("", Validators.required),
      site: new FormControl("", Validators.required),
      application: new FormControl("", Validators.required),
    }),
    configuration_details: new FormGroup({
      line_current: new FormControl(0),
      device_name: new FormControl("", Validators.required),
      mac_address: new FormControl("", Validators.required),
      motor_make: new FormControl("", Validators.required),
      equipment_location: new FormControl(""),
      frame: new FormControl(""),
      rated_amb_temp: new FormControl(""),
      rated_amb_temp_unit: new FormControl("C"), // make it dynamic later if ambient temp was added
      insulation_class: new FormControl("", Validators.required),
      serial_number: new FormControl(
        { value: "", disabled: true },
        Validators.required,
      ),
      rated_power: new FormControl(34, Validators.required),
      rated_power_unit: new FormControl("KW", Validators.required), // add more units later
      rated_rpm: new FormControl(34, Validators.required),
      rated_rpm_type: new FormControl("fixed", Validators.required), // add 2 new controls for max and min if rpm type is changed
      rated_voltage: new FormControl(23),
      percentage_eff: new FormControl(5),
      line_freq: new FormControl("50", Validators.required),
      power_factor: new FormControl("3"),
      duty: new FormControl("1"),
      bearing_type: new FormControl("sleeve_bearing"),
      de_number: new FormControl({ value: "NA", disabled: true }),
      nde_number: new FormControl({ value: "NA", disabled: true }),
      pole_pairs: new FormControl(""),

      pinion_gear_details: new FormGroup({
        no_of_stages: new FormControl(""),
        p_gear_rpm: new FormControl(""),
        p_gear_teeth: new FormControl(""),
        p_bearing_type: new FormControl("sleeve_bearing"),
        p_deb_no: new FormControl({ value: "NA", disabled: true }),
        p_ndeb_no: new FormControl({ value: "NA", disabled: true }),
      }),

      output_gear_details: new FormGroup({
        no_of_opt_gears: new FormControl(""),
        o_gear_rpm: new FormControl(""),
        o_gear_teeth: new FormControl(""),
        o_bearing_type: new FormControl("sleeve_bearing"),
        o_deb_no: new FormControl({ value: "NA", disabled: true }),
        o_ndeb_no: new FormControl({ value: "NA", disabled: true }),
      }),

      gmf_details: new FormGroup({
        is_gmf: new FormControl(""),
        no_of_gmf: new FormControl(""),
        gmf_list: new FormArray([]),
      }),
    }),
    drive_details: new FormGroup({
      drive_type: new FormControl("direct"),
      driver_pulley_diameter: new FormControl(""),
      driven_pulley_diameter: new FormControl(""),
      dist_bt_shaft: new FormControl(""),
      belt_no: new FormControl(""),
      belt_length: new FormControl(""),
    }),
    other_configurations: new FormGroup({
      sensor_mounting: new FormControl("PARALLEL_SIDE"),
      RTD_1: new FormControl("MS"),
      RTD_2: new FormControl("na"),
      RTD_3: new FormControl("na"),
      temp_1_comp: new FormControl("0"),
      temp_2_comp: new FormControl("0"),
      temp_3_comp: new FormControl("0"),
      vib_1_comp: new FormControl("1"),
      vib_2_comp: new FormControl("1"),
      vib_3_comp: new FormControl("1"),
    }),
    device_push_rate_configuration: new FormGroup({
      summaryFileRefreshRate: new FormControl(15),
      fullRefreshFileRate: new FormControl(60),
      scanRate: new FormControl(15),
      odr_normal: new FormControl("Normal"),
      odr_high_res: new FormControl("Normal"),
      anomalyAnalysisTime: new FormControl(180),
      anomalyCounter: new FormControl(1),
      machine_threshold_status: new FormControl(0.0),
    }),
    network_configuration: new FormGroup({
      connectivity_type: new FormControl("Wifi"),
      wifi_ssid: new FormControl(""),
      wifi_password: new FormControl(""),
    }),
  });
  sites: any = [];
  applications: any[] = [];
  bearing_numbers: any[] = [];
  device_inventory: any[] = []; // contains all devices associated to USER_ID
  filtered_devices: any[] = []; // contains only un-associated devices of USER_ID
  available_devices: any[] = []; // contains only un-associated devices of USER_ID
  saved_rotating_asset_data: any = {};
  client_id: number = null;

  private activated_route = inject(ActivatedRoute);
  private piezeo_service = inject<any>(PiezeoService);

  constructor(
    private snackBar: MatSnackBar,
    private router: Router,
  ) {
    effect(() => {
      // this.current_sensor = this.all_sensors.filter(s=>s.form_configured==false)[0] || null;
    });
  }

  ngOnDestroy() {
    this.piezeo_service.reset();
  }

  async ngOnInit() {
    try {
      const {
        snapshot: { queryParams },
      } = this.activated_route;
      // this.piezeo_service.setData(this.data); // for local testing
      // this.piezeo_service.setAllSensors(this.data.data); // for local testing
      // this.all_sensors = this.data.data; // for local testing
      // this.subsystem_data = this.data.suybsystem_table_data; // for local testing

      this.all_sensors = this.piezeo_service.getAllSensors();
      this.emsaver_sensors = this.piezeo_service.getEmsaverSensors();
      this.configured_gateways = this.piezeo_service.getConfiguredGateways();
      this.subsystem_data = this.piezeo_service.getData().suybsystem_table_data;
      let role = localStorage.getItem("role");
      switch (role) {
        case "CLIENT":
          this.client_id = parseInt(localStorage.getItem("user_id"));
          break;
      }
      // this.fetchSensorFormData(0); // fetches for sensor_number 0 initially
      this.current_sensor =
        this.all_sensors.filter((s) => s.form_configured == false)[0] || null;

      this.detectAxisChange();
      await Promise.all([
        this.fetchSites(),
        this.fetchBearingNumbers(),
        this.fetchAllInventory(),
        this.fetchSystemGateways(),
      ]);

      const general_details = this.rotating_asset_form.get("general_details");

      general_details.get("site").setValue(this.subsystem_data.site_id);
      general_details
        .get("application")
        .setValue(this.subsystem_data.application_id);
      general_details
        .get("asset_name")
        .setValue(this.subsystem_data.subsystem_name);
      general_details.get("site").disable({ onlySelf: true });
      general_details.get("application").disable({ onlySelf: true });
      general_details.get("asset_name").disable({ onlySelf: true });

      this.handleSiteChange({ value: this.subsystem_data.site_id });
    } catch (error) {
      console.log(error);
    }
  }

  openPiezeoPreview() {
    this.modal_type.set("pt_preview");
  }
  closePiezeoPreview() {
    this.modal_type.set("");
  }

  /* ---------------------------------------------------------------------PIEZEO LOGIC STARTS------------------------------------------------------- */
  detectAxisChange() {
    this.piezeoConfigForm
      .get("no_of_axis")
      ?.valueChanges.subscribe((newValue: number) => {
        this.piezeoConfigForm.setControl("sensors_info", new FormGroup({})); // clear all errors and values

        this.total_no_of_axis = Array.from(
          { length: newValue },
          (_, i) => i + 1,
        );

        const sensorsInfoGroup = this.piezeoConfigForm.get(
          "sensors_info",
        ) as FormGroup;
        if (!sensorsInfoGroup) return;

        // Clear existing controls
        Object.keys(sensorsInfoGroup.controls).forEach((key) => {
          sensorsInfoGroup.removeControl(key);
        });

        // Add new controls
        for (let i = 1; i <= newValue; i++) {
          sensorsInfoGroup.addControl(
            `sensor_${i}_axis`,
            new FormControl("", Validators.required),
          );
          sensorsInfoGroup.addControl(
            `sensor_${i}_serial`,
            new FormControl("", Validators.required),
          );
        }

        // ✅ Dynamically apply the group-level validator
        const axis_keys = Object.keys(sensorsInfoGroup.controls).filter((key) =>
          key.endsWith("_axis"),
        );
        const serial_keys = Object.keys(sensorsInfoGroup.controls).filter(
          (key) => key.endsWith("_serial"),
        );

        sensorsInfoGroup.setValidators([
          compareSameAxis(...axis_keys),
          checkSamePiezeoSerial(...serial_keys),
        ]);
        sensorsInfoGroup.updateValueAndValidity({ emitEvent: false });
      });
  }

  clearCurrentPiezeoConfig() {
    this.piezeoConfigForm.reset();
  }

  //Called when user randomly selects any sensor and we check if its config was already pushed or not
  fillPiezeoConfig(index: number) {
    const { no_of_axis, sensors_info } = this.all_sensors[index];
    this.piezeoConfigForm.setControl("sensors_info", new FormGroup({}));
    this.piezeoConfigForm
      .get("no_of_axis")
      .setValue(no_of_axis, { emitEvent: false });
    if (!no_of_axis) return;

    this.total_no_of_axis = Array.from({ length: no_of_axis }, (_, i) => i + 1);

    const sensorsInfoGroup = this.piezeoConfigForm.get(
      "sensors_info",
    ) as FormGroup;
    if (!sensorsInfoGroup) return;

    // Clear existing controls
    Object.keys(sensorsInfoGroup.controls).forEach((key) => {
      sensorsInfoGroup.removeControl(key);
    });

    // Add new controls
    for (let i = 1; i <= no_of_axis; i++) {
      console.log(
        `Adding sensor_${i}_axis:::${
          sensors_info[`sensor_${i}_axis`]
        },sensor_${i}_serial:::${sensors_info[`sensor_${i}_serial`]}`,
      );
      sensorsInfoGroup.addControl(
        `sensor_${i}_axis`,
        new FormControl(sensors_info[`sensor_${i}_axis`], Validators.required),
      );
      sensorsInfoGroup.addControl(
        `sensor_${i}_serial`,
        new FormControl(
          sensors_info[`sensor_${i}_serial`],
          Validators.required,
        ),
      );
    }

    // ✅ Dynamically apply the group-level validator
    const axis_keys = Object.keys(sensorsInfoGroup.controls).filter((key) =>
      key.endsWith("_axis"),
    );
    const serial_keys = Object.keys(sensorsInfoGroup.controls).filter((key) =>
      key.endsWith("_serial"),
    );

    sensorsInfoGroup.setValidators([
      compareSameAxis(...axis_keys),
      // checkSamePiezeoSerial(...serial_keys),
    ]);
    sensorsInfoGroup.updateValueAndValidity({ emitEvent: false });
  }

  logInvalidControls(form: FormGroup, parentKey: string = "") {
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);

      if (control instanceof FormGroup) {
        this.logInvalidControls(
          control,
          parentKey ? `${parentKey}.${key}` : key,
        );
      } else if (control?.invalid) {
        console.warn(
          `❌ Invalid Field: ${parentKey ? parentKey + "." : ""}${key}`,
          control.errors,
        );
      }
    });
  }

  addPiezeo(sensor: any, gateway: any) {
    if (sensor.configured && sensor.is_new == false) {
      alert("Available soon on sensor level!");
      return;
    }
    this.current_sensor = sensor;
    this.modal_type.set("piezeo_config");
    this.onSensorChange();
    this.current_gateway = gateway;
  }
  closePiezoAdditionModal(data: any) {
    this.modal_type.set("");
    this.all_sensors = this.all_sensors.map((s) =>
      s.sensor_number == data.sensor_number ? data : s,
    );
    this.piezeo_service.setAllSensors(this.all_sensors);
  }

  /* ---------------------------------------------------------------------PIEZEO LOGIC ENDS------------------------------------------------------- */
  /* ---------------------------------------------------------------------GATEWAY LOGIC STARTS------------------------------------------------------- */
  addGateway() {
    this.modal_type.set("add_gateway");
  }
  closeGatewayAdditionModal(data: null | any) {
    console.log(data);
    this.modal_type.set("");
    if (!data) return;

    this.configured_gateways = this.piezeo_service.getConfiguredGateways(); // get updated array of configured
    console.log("UPDATED GATEWAYS");
    console.log(this.configured_gateways);
  }

  /*
      - Edit any configured gatway
      - This does not handles channel related logic 
  */
  editGateway(gateway: any) {
    console.log(gateway);
    this.current_gateway = gateway;
    this.modal_type.set("edit_gateway");
  }

  async deleteGateway(gateway) {
    let curr_gid = gateway.gatewayAddress.id;

    this.configured_gateways = this.configured_gateways.filter(
      (g) => g.gatewayAddress.id != curr_gid,
    );
    /* 
       - Mark all sensors assocaited to this gateway a initial case 
    */
    this.all_sensors.map((s) => {
      if (s.configured && s.gateway_id == curr_gid) {
        s.gateway_id = null;
        s.configured = false;
        s.associated = false;
        s.no_of_axis = null;
        s.sensors_info = {};
        if (s.is_temperature_added) {
          s.is_temperature_added = 0;
          s.temperature_channel = null;
        }
        return s;
      } else {
        return s;
      }
    });

    /*
       - Update all_sensors and configured_gateways
    */
    this.piezeo_service.setAllSensors(this.all_sensors);
    this.piezeo_service.deleteGatewayInConfiguredList(curr_gid);
    this.piezeo_service.setGatewaysToDelete(gateway);
  }

  async fetchSystemGateways() {
    try {
      let gateways = await this.piezeo_service.fetchSystemGateways(
        `${this.url_prefix}/api/get-system-gateways/${this.subsystem_data.subsystem_id}`,
      );
      console.log(gateways);
      this.piezeo_service.setInitialConfiguredGateways(gateways);
      this.configured_gateways = gateways;
    } catch (error) {}
  }

  /* ---------------------------------------------------------------------GATEWAY LOGIC ENDS------------------------------------------------------- */
  /* ---------------------------------------------------------------------ASSOCIATE DEVICE TO GATEWAY LOGIC STARTS------------------------------------------------------- */
  associateSensorToGateway(gateway) {
    this.current_gateway = gateway;
    this.modal_type.set("associate_device_to_gateway");
  }

  handleGatewayAssociationClose(data) {
    try {
      const { gateway_data, all_sensors } = data;
      this.configured_gateways = this.configured_gateways.map((g) =>
        g.gatewayAddress.id == gateway_data.id ? gateway_data : g,
      );
      this.all_sensors = all_sensors;
    } catch (error) {
    } finally {
      console.log("FINAL ALL SENSOSRS AND CONFIG GATEWAYS");
      console.log(this.all_sensors);
      console.log(this.configured_gateways);
      this.modal_type.set("");
    }
  }
  /* ---------------------------------------------------------------------ASSOCIATE DEVICE TO GATEWAY LOGIC ENDS------------------------------------------------------- */

  /* ---------------------------------------------------------------------ROTATING ASSET FORM STARTS------------------------------------------------------- */

  async fetchSites() {
    let url = `${BaseUrlConstantsService.GET_SITES}/${this.encryptUserId("2")}`;
    this.sites = await this.piezeo_service.fetchUserSites(url);
  }

  async fetchBearingNumbers() {
    let url = `${BaseUrlConstantsService.ASSETS_BEARING}`;
    this.bearing_numbers =
      await this.piezeo_service.fetchAllBearingNumbers(url);
  }

  async fetchAllInventory() {
    let url = `${BaseUrlConstantsService.CLIENT_DEVICES}/${this.encryptUserId(
      this.client_id + "",
    )}`;
    this.device_inventory =
      await this.piezeo_service.fetchAllDeviceInventory(url);
    this.filtered_devices = this.device_inventory;
    this.available_devices = this.filtered_devices;
  }

  // To construct default map to hold rotating asset data for each sensor used to fill forms for rotating asset
  constructRotatingAssetDataForm() {
    this.all_sensors.map(
      (s) => (this.saved_rotating_asset_data[s.sensor_number] = {}),
    );
  }

  handleSiteChange(site: any) {
    this.applications = this.sites.filter(
      (s) => s.id == site.value,
    )[0]?.applications;
  }
  // When RPM is changed from fixed to variable
  onRpmChange(event: MatRadioChange) {
    let rpm_type = event.value;
    const configuration_details = this.rotating_asset_form.get(
      "configuration_details",
    ) as FormGroup;
    if (rpm_type == "fixed") {
      configuration_details.removeControl("min_rpm");
      configuration_details.removeControl("max_rpm");
    } else {
      configuration_details.addControl(
        "min_rpm",
        new FormControl(0, Validators.required),
      );
      configuration_details.addControl(
        "max_rpm",
        new FormControl(0, Validators.required),
      );
    }
  }

  // When bearing/sleeve bearing is selected
  onBearingChange(event: MatRadioChange, type = "bearing_type") {
    if (type == "bearing_type") {
      // Non Gear Assets bearing type
      const configuration_details = this.rotating_asset_form.get(
        "configuration_details",
      ) as FormGroup;
      if (event.value == "sleeve_bearing") {
        configuration_details.get("nde_number").patchValue("NA");
        configuration_details.get("nde_number").disable({ onlySelf: true });
        configuration_details.get("de_number").patchValue("NA");
        configuration_details.get("de_number").disable({ onlySelf: true });
      } else {
        configuration_details.get("nde_number").patchValue("0");
        configuration_details.get("nde_number").enable();
        configuration_details.get("de_number").patchValue("0");
        configuration_details.get("de_number").enable();
      }
    } else if (type == "p_bearing_type") {
      // Pinion Gear Details
      const configuration_details = this.rotating_asset_form.get(
        "configuration_details",
      ) as FormGroup;
      if (event.value == "sleeve_bearing") {
        configuration_details
          .get("pinion_gear_details.p_ndeb_no")
          .patchValue("NA");
        configuration_details
          .get("pinion_gear_details.p_ndeb_no")
          .disable({ onlySelf: true });
        configuration_details
          .get("pinion_gear_details.p_deb_no")
          .patchValue("NA");
        configuration_details
          .get("pinion_gear_details.p_deb_no")
          .disable({ onlySelf: true });
      } else {
        configuration_details
          .get("pinion_gear_details.p_ndeb_no")
          .patchValue("0");
        configuration_details.get("pinion_gear_details.p_ndeb_no").enable();
        configuration_details
          .get("pinion_gear_details.p_deb_no")
          .patchValue("0");
        configuration_details.get("pinion_gear_details.p_deb_no").enable();
      }
    } else {
      //  Output Gear Details
      const configuration_details = this.rotating_asset_form.get(
        "configuration_details",
      ) as FormGroup;
      if (event.value == "sleeve_bearing") {
        configuration_details
          .get("output_gear_details.o_ndeb_no")
          .patchValue("NA");
        configuration_details
          .get("output_gear_details.o_ndeb_no")
          .disable({ onlySelf: true });
        configuration_details
          .get("output_gear_details.o_deb_no")
          .patchValue("NA");
        configuration_details
          .get("output_gear_details.o_deb_no")
          .disable({ onlySelf: true });
      } else {
        configuration_details
          .get("output_gear_details.o_ndeb_no")
          .patchValue("0");
        configuration_details.get("output_gear_details.o_ndeb_no").enable();
        configuration_details
          .get("output_gear_details.o_deb_no")
          .patchValue("0");
        configuration_details.get("output_gear_details.o_deb_no").enable();
      }
    }
  }

  // When drive type is changed from direct to pulley
  onDriveTypeChange(event: MatRadioChange) {
    const drive_details = this.rotating_asset_form.get(
      "drive_details",
    ) as FormGroup;

    if (event.value == "direct") {
      drive_details.removeControl("driver_pulley_diameter");
      drive_details.removeControl("driven_pulley_diameter");
      drive_details.removeControl("dist_bt_shaft");
      drive_details.removeControl("belt_no");
      drive_details.removeControl("belt_length");
    } else {
      drive_details.removeControl("driver_pulley_diameter");
      drive_details.removeControl("driven_pulley_diameter");
      drive_details.removeControl("dist_bt_shaft");
      drive_details.removeControl("belt_no");
      drive_details.removeControl("belt_length");
    }
  }

  // When MAC address is changed
  onMacAddressChange(device: any) {
    this.rotating_asset_form
      .get("configuration_details.serial_number")
      .patchValue(device.value.name);

    if (device.value.master_type != 4)
      this.updatedRTDs(device.value.master_type);
  }

  // When current sensor is changed on Rotating Asset Form
  onSensorSelect(sensor: any, is_inital_load = false) {
    if (sensor.form_configured == true) {
      alert("Already Configured");
      return;
    }
    if (sensor.sensor_number == this.current_sensor.sensor_number) return;
    this.current_sensor = sensor;
    this.onSensorChange();
    if (
      this.saved_rotating_asset_data[sensor.sensor_number] &&
      Object.keys(this.saved_rotating_asset_data[sensor.sensor_number]).length >
        0
    ) {
      this.fillRotatingAssetForm(
        sensor.sensor_number,
        this.current_sensor.assetType,
      );
    } else {
      console.log(this.current_sensor.sensor_number);
      this.fetchSensorFormData(this.current_sensor.sensor_number);
    }
  }

  onSensorChange() {
    const { assetType, subType } = this.current_sensor;

    // DISABLE GEAR FIELDS INITIALLY
    const configuration_details = this.rotating_asset_form.get(
      "configuration_details",
    ) as FormGroup;

    const pinion = configuration_details.get(
      "pinion_gear_details",
    ) as FormGroup;
    const output = configuration_details.get(
      "output_gear_details",
    ) as FormGroup;
    const gmf_details = configuration_details.get("gmf_details") as FormGroup;

    pinion.disable({ emitEvent: false });
    output.disable({ emitEvent: false });
    gmf_details.disable({ emitEvent: false });

    // Reset form state AFTER saving - use the disable approach
    this.resetUncommonFields();

    switch (assetType) {
      case "MOTOR":
        this.configureMotorFields();
        break;
      case "GEAR":
        this.configureGearFields();
        break;
      case "PUMP":
        this.configurePumpFields();
        break;
      case "FAN_BLOWER":
        // this.configureMotorFields();
        break;
      case "COMPRESSOR":
        // this.configureMotorFields();
        break;
      case "JOURNAL_BEARING":
        // this.configureMotorFields();
        break;
    }
  }

  fillRotatingAssetForm(sensor_number: number, assetType: string) {
    const saved_data = this.saved_rotating_asset_data[sensor_number];
    this.rotating_asset_form.patchValue(saved_data);
  }

  async fetchSensorFormData(sensor_number) {
    try {
      const data = await this.piezeo_service.fetchSensorFormData(
        `${this.url_prefix}/api/get_rotating_asset_form/${this.subsystem_data.subsystem_id}/${sensor_number}`,
      );
      this.saved_rotating_asset_data[sensor_number] = data;
      this.rotating_asset_form.patchValue(data);
      this.rotating_asset_form.markAsPristine();

      const configuration_details = this.rotating_asset_form.get(
        "configuration_details",
      ) as FormGroup;

      //HANDLE SLEEVE/BALL_BEARING
      const { bearing_type, rated_rpm_type } = data.configuration_details;
      const { sensor_type } = data.general_details;

      if (bearing_type == "sleeve_bearing") {
        configuration_details.get("nde_number").patchValue("NA");
        configuration_details.get("nde_number").disable({ onlySelf: true });
        configuration_details.get("de_number").patchValue("NA");
        configuration_details.get("de_number").disable({ onlySelf: true });
      } else {
        const { de_number, nde_number } = data.configuration_details;
        configuration_details.get("nde_number").patchValue(nde_number);
        configuration_details.get("de_number").patchValue(de_number);
      }

      // HANLDE RATED RPM
      if (rated_rpm_type == "variable") {
        const { min_rpm, max_rpm } = data.configuration_details;
        configuration_details.addControl(
          "min_rpm",
          new FormControl(min_rpm, Validators.required),
        );
        configuration_details.addControl(
          "max_rpm",
          new FormControl(max_rpm, Validators.required),
        );
      }

      if (sensor_type == "GEAR") {
      }
    } catch (error) {
      console.log(error);
    }
  }

  // FUNCTIONS TO CONFIGURE FORM FIELDS BASED ON ASSET TYPE

  configureMotorFields() {
    this.setPolePairs();
    this.resetCommonFields();
  }

  configurePumpFields() {
    const configuration_details = this.rotating_asset_form.get(
      "configuration_details",
    ) as FormGroup;
    configuration_details.addControl(
      "no_of_blades",
      new FormControl(0, Validators.required),
    );

    this.resetCommonFields();
    this.setPolePairs();
  }

  configureGearFields() {
    const configuration_details = this.rotating_asset_form.get(
      "configuration_details",
    ) as FormGroup;

    const pinion = configuration_details.get(
      "pinion_gear_details",
    ) as FormGroup;
    const output = configuration_details.get(
      "output_gear_details",
    ) as FormGroup;
    const gmf_details = configuration_details.get("gmf_details") as FormGroup;

    pinion.enable({ emitEvent: false });
    output.enable({ emitEvent: false });
    gmf_details.enable({ emitEvent: false });

    pinion.get("p_deb_no").disable({ onlySelf: true });
    pinion.get("p_ndeb_no").disable({ onlySelf: true });

    output.get("o_deb_no").disable({ onlySelf: true });
    output.get("o_ndeb_no").disable({ onlySelf: true });

    configuration_details.get("bearing_type").disable({ onlySelf: true });
    configuration_details.get("de_number").disable({ onlySelf: true });
    configuration_details.get("nde_number").disable({ onlySelf: true });
    this.rotating_asset_form.get("drive_details").disable({ onlySelf: true });

    gmf_details.get("no_of_gmf").valueChanges.subscribe(() => {
      this.handleGMFList();
    });
    gmf_details.get("is_gmf").valueChanges.subscribe(() => {
      this.handleGMFList();
    });

    this.setPolePairs(); // add/remove pole pairs as this field is for MOTOR only

    console.log(this.rotating_asset_form);
  }

  get gmf_list(): FormArray {
    return this.rotating_asset_form.get(
      "configuration_details.gmf_details.gmf_list",
    ) as unknown as FormArray;
  }

  get gmf_enabled() {
    return this.rotating_asset_form.get(
      "configuration_details.gmf_details.is_gmf",
    );
  }

  handleGMFList() {
    const configuration_details = this.rotating_asset_form.get(
      "configuration_details",
    ) as FormGroup;
    const gmf_details = configuration_details.get("gmf_details") as FormGroup;
    const is_gmf = gmf_details.get("is_gmf")?.value;
    const list = this.gmf_list;

    if (is_gmf == "gmf_disabled") {
      while (list.length !== 0) {
        list.removeAt(0);
      }
      gmf_details.get("no_of_gmf")?.setValue(0, { emitEvent: false });
      return; // Exit the function early
    }

    const count =
      this.rotating_asset_form.get(
        "configuration_details.gmf_details.no_of_gmf",
      )?.value || 0;
    const currentLength = this.gmf_list.length;

    console.log(`New Count ${count}  Current Count ${currentLength}`);

    if (count > currentLength) {
      for (let i = currentLength; i < count; i++) {
        this.gmf_list.push(
          new FormGroup({
            gmf_value: new FormControl("", Validators.required),
            number_for_gmf: new FormControl("", Validators.required),
          }),
        );
      }
    }

    // Remove extra rows
    else if (count < currentLength) {
      for (let i = currentLength - 1; i >= count; i--) {
        this.gmf_list.removeAt(i);
      }
    }
  }

  resetCommonFields() {
    const configuration_details = this.rotating_asset_form.get(
      "configuration_details",
    ) as FormGroup;
    /*
       -  previous assetType was not gear hence 
       - bearing_type,de_number,nde_number is already present 
       - in else case add them
       - //ly remove gear formGroups if exists
    */
    if (!configuration_details.get("bearing_type")) {
      configuration_details.addControl(
        "bearing_type",
        new FormControl("ball_bearing"),
      );
      configuration_details.addControl("de_number", new FormControl("0"));
      configuration_details.addControl("nde_number", new FormControl("0"));
    }

    if (!configuration_details.get("drive_details")) {
      let drive_details = new FormGroup({
        drive_type: new FormControl("direct"), // add new controls if selected type is pulley
      });
      configuration_details.addControl("drive_details", drive_details);
    }

    // if (configuration_details.get("pinion_gear_details"))
    //   configuration_details.removeControl("pinion_gear_details");
    // if (configuration_details.get("output_gear_details"))
    //   configuration_details.removeControl("output_gear_details");
    // if (configuration_details.get("gmf_details"))
    //   configuration_details.removeControl("gmf_details");
  }

  resetUncommonFields() {
    try {
      // ✅ Step 1: Get values BEFORE any changes
      const general_details = this.rotating_asset_form.get(
        "general_details",
      ) as FormGroup;
      const { asset_name, site, application } = general_details.getRawValue();

      // ✅ Step 2: Clear and reset configuration fields
      const config = this.rotating_asset_form.get(
        "configuration_details",
      ) as FormGroup;

      // Fields to clear
      const fieldsToClear = [
        "device_name",
        "motor_make",
        "frame",
        "rated_amb_temp",
        "insulation_class",
        "rated_power",
        "rated_rpm",
        "rated_voltage",
        "percentage_eff",
        "line_freq",
        "power_factor",
        "duty",
        "line_current",
        "equipment_location",
        "pole_pairs",
      ];

      // Clear all specified fields
      fieldsToClear.forEach((field) => {
        const control = config.get(field);
        if (control) {
          control.reset(null, { emitEvent: false });
          control.markAsUntouched();
          control.markAsPristine();
        }
      });

      // Reset other form groups
      const driveDetails = this.rotating_asset_form.get(
        "drive_details",
      ) as FormGroup;
      if (driveDetails) {
        Object.keys(driveDetails.controls).forEach((key) => {
          driveDetails.get(key)?.reset(null, { emitEvent: false });
        });
      }
      driveDetails.get("drive_type").setValue("direct"); // Sets drive_type to 'direct' default

      const otherConfigs = this.rotating_asset_form.get(
        "other_configurations",
      ) as FormGroup;
      if (otherConfigs) {
        otherConfigs.reset(
          {
            sensor_mounting: "PARALLEL_SIDE",
            RTD_1: "MS",
            RTD_2: "na",
            RTD_3: "na",
            temp_1_comp: "0",
            temp_2_comp: "0",
            temp_3_comp: "0",
            vib_1_comp: "1",
            vib_2_comp: "1",
            vib_3_comp: "1",
          },
          { emitEvent: false },
        );
      }

      // ✅ Step 3: Disable general fields if they have valid values
      if (this.checkValidInput(asset_name)) {
        general_details.get("asset_name")?.disable({ emitEvent: false });
      }
      if (this.checkValidInput(site)) {
        general_details.get("site")?.disable({ emitEvent: false });
      }
      if (this.checkValidInput(application)) {
        general_details.get("application")?.disable({ emitEvent: false });
      }

      // ✅ Step 4: Disable serial number (keep it disabled as per form setup)
      config.get("serial_number")?.disable({ emitEvent: false });

      // ✅ Step 5: Reset form state
      this.rotating_asset_form.markAsPristine({ emitEvent: false });
      this.rotating_asset_form.markAsUntouched({ emitEvent: false });

      // ✅ Step 6: Manually trigger validation update
      this.rotating_asset_form.updateValueAndValidity({ emitEvent: false });

      console.log("Form reset successfully");
    } catch (error) {
      console.error("Error during form reset:", error);
    }
  }

  setPolePairs() {
    // ONLY AVAIALABLE IN MOTOR AS PER NOW
    if (this.current_sensor.assetType == "MOTOR") {
      if (!this.rotating_asset_form.get("configuration_details.pole_pairs")) {
        console.log("Adding Pole Pairs");
        (
          this.rotating_asset_form.get("configuration_details") as FormGroup
        ).addControl("pole_pairs", new FormControl(0));
      }
    } else {
      // if exists then only delete the control
      // on executes when previous type was motor
      if (this.rotating_asset_form.get("configuration_details.pole_pairs")) {
        console.log("Removing Pole Pairs");
        (
          this.rotating_asset_form.get("configuration_details") as FormGroup
        ).removeControl("pole_pairs");
      }
    }
  }

  saveCurrentAsset() {
    // Validate form before saving
    if (this.rotating_asset_form.invalid) {
      this.showSnackBar("Form is invalid, cannot save");
      this.logInvalidControls(this.rotating_asset_form);
      return;
    }

    let { master_type } = this.current_sensor;
    let data: any = this.rotating_asset_form.getRawValue();
    const { id, mac_address, name } = data.configuration_details.mac_address;

    delete data.configuration_details.mac_address.updated_at;
    delete data.configuration_details.mac_address.created_at;
    delete data.configuration_details.mac_address.device_status;
    delete data.configuration_details.mac_address.wifi_ready_to_update;
    delete data.configuration_details.mac_address.ready_to_update;
    delete data.configuration_details.mac_address.device_cloud_api;
    delete data.configuration_details.mac_address.pushed_firmware_version;
    delete data.configuration_details.mac_address.user_id;
    delete data.configuration_details.mac_address.installed_firmware_version;
    delete data.configuration_details.mac_address.wifi_pushed_version;
    delete data.configuration_details.mac_address.wifi_installed_version;
    delete data.configuration_details.mac_address.health_updated_at;
    delete data.configuration_details.mac_address.health_detail;
    delete data.configuration_details.mac_address.health_counter;
    delete data.configuration_details.mac_address.health;
    delete data.configuration_details.mac_address.status;
    delete data.configuration_details.mac_address.gateway_url;
    delete data.configuration_details.mac_address.platform;

    if (master_type == 4) {
      delete data.network_configuration;
      delete data.device_push_rate_configuration;
    }

    let asset_id = null;
    if (
      this.saved_rotating_asset_data[this.current_sensor.sensor_number]
        ?.general_details?.asset_id
    )
      asset_id =
        this.saved_rotating_asset_data[this.current_sensor.sensor_number]
          ?.general_details.asset_id;

    this.saved_rotating_asset_data[this.current_sensor.sensor_number] = data;

    if (asset_id) {
      // this.saved_rotating_asset_data[this.current_sensor.sensor_number].is_updated = true;
      // this.saved_rotating_asset_data[this.current_sensor.sensor_number]
      //   .general_details.asset_id = asset_id;
    } else {
      // for new sensor added
      this.saved_rotating_asset_data[this.current_sensor.sensor_number].is_new =
        true;
    }

    this.saved_rotating_asset_data[
      this.current_sensor.sensor_number
    ].sensor_details = this.current_sensor;

    console.log(this.all_sensors);
    console.log(this.current_sensor);
    if (master_type == 4) {
      this.all_sensors = this.all_sensors.map((s) =>
        s.sensor_number == this.current_sensor.sensor_number
          ? {
              ...s,
              form_configured: true,
              inventory_id: id,
              mac_address,
              serial_number: name,
            }
          : s,
      );
    } else {
      this.emsaver_sensors = this.emsaver_sensors.map((s) =>
        s.sensor_number == this.current_sensor.sensor_number
          ? {
              ...s,
              form_configured: true,
              inventory_id: id,
              mac_address,
              serial_number: name,
            }
          : s,
      );
    }
    this.current_sensor.form_configured = true;

    const selected_mac_id = (data.configuration_details.mac_address as any).id;

    if (this.current_sensor.master_type == 4)
      this.updateAvailableMacAddresses(selected_mac_id);
    else
      this.updateEmsaverAvailableMacAddresses(
        selected_mac_id,
        this.current_sensor.master_type,
      );

    // Show success message
    this.showSnackBar("✅ Asset saved and form reset successfully");
  }

  resetForm() {
    // this.rotating_asset_form.reset();
    this.resetUncommonFields();

    this.saved_rotating_asset_data[this.current_sensor.sensor_number] = {};

    this.all_sensors = this.all_sensors.map((s) =>
      s.sensor_number == this.current_sensor.sensor_number
        ? { ...s, form_configured: false }
        : s,
    );
  }

  async saveCompleteConfiguration() {
    let is_invalid = 0;

    for (let s of this.all_sensors) {
      if (!s.associated || !s.configured || !s.form_configured) {
        is_invalid = 1;
        break;
      }
    }

    if (is_invalid) {
      this.showSnackBar("Configure all Assets first!");
      return;
    }

    const body = {
      rotating_asset_data: this.saved_rotating_asset_data,
      gateway_data: this.configured_gateways,
      subsystem_data: this.subsystem_data,
      device_position: [...this.all_sensors, ...this.emsaver_sensors],
    };

    console.log(body);

    try {
      // 👇 CALL THE API FUNCTION
      const resp = await this.piezeo_service.savePiezo(
        `${this.url_prefix}/api/update-subsystem`,
        body,
      );

      if (resp.success) {
        this.showSnackBar(resp.message || "Saved Successfully!");

        // 👇 NAVIGATE AFTER SUCCESS
        // this.router.navigate(["/device-reports"]);
      } else {
        this.showSnackBar("Failed to save configuration!");
      }
    } catch (err) {
      console.error(err);
      this.showSnackBar("Something went wrong!");
    }
  }

  /* ---------------------------------------------------------------------ROTATING ASSET FORM ENDS------------------------------------------------------- */

  /* ------------------------------------------------------------------------------------------------------------------------------------------------------- */
  /* ------------------------------------------------------------------------------------------------------------------------------------------------------- */
  /* ---------------------------------------------------------------------EMSAVERS FORMS LOGIC STARTS------------------------------------------------------- */
  /* ------------------------------------------------------------------------------------------------------------------------------------------------------- */
  /* ------------------------------------------------------------------------------------------------------------------------------------------------------- */
  emsaver_sensors = [];
  emsaver_device_inventory = {
    emsaver_mini: [],
    emsaver_std: [],
    emsaver_rtds: [],
  };
  emsaver_filtered_devices = {
    emsaver_mini: [],
    emsaver_std: [],
    emsaver_rtds: [],
  };
  emsaver_available_devices = {
    emsaver_mini: [],
    emsaver_std: [],
    emsaver_rtds: [],
  };

  hidePassword = true;
  odr_options = [
    { value: "HIGH", name: "High(3400Hz)" },
    { value: "NORMAL", name: "Normal(1700Hz)" },
    { value: "LOW", name: "Low(200Hz)" },
    { value: "EXTRALOW", name: "Extra Low(100Hz)" },
    { value: "EXTRAEXTRALOW", name: "Extra Extra Low(50Hz)" },
    { value: "ULTRALOW", name: "Ultra Low" },
    { value: "ULTRAULTRALOW", name: "Ultra Ultra Low" },
  ];

  async onTabChange(event: MatTabChangeEvent) {
    const { index } = event;

    if (index != 2) return;

    this.emsaver_sensors = this.piezeo_service.getEmsaverSensors();
    this.handleEmsaverFields();
    this.current_sensor = this.emsaver_sensors.filter(
      (s) => s.form_configured == false,
    )[0];
    this.onSensorChange();
    this.onSensorSelect(this.current_sensor, true);
    await this.fetchEmsaverDeviceInventory();
  }

  async fetchEmsaverDeviceInventory() {
    let url = `https://qaapi.enertics.ca/middleware/userinventory/8922307`; // Change this;

    this.emsaver_device_inventory =
      await this.piezeo_service.fetchEmsaverDeviceInventory(url);
    this.emsaver_filtered_devices = this.emsaver_device_inventory;
    this.emsaver_available_devices = this.emsaver_filtered_devices;
  }

  updateEmsaverAvailableMacAddresses(id, master_type) {
    if (master_type == 1) {
      let old_devices = this.emsaver_filtered_devices.emsaver_mini;
      this.emsaver_filtered_devices.emsaver_mini = old_devices.filter(
        (d) => d.id != id,
      );
    }
    if (master_type == 2) {
      let old_devices = this.emsaver_filtered_devices.emsaver_std;
      this.emsaver_filtered_devices.emsaver_std = old_devices.filter(
        (d) => d.id != id,
      );
    }
    if (master_type == 3) {
      let old_devices = this.emsaver_filtered_devices.emsaver_rtds;
      this.emsaver_filtered_devices.emsaver_rtds = old_devices.filter(
        (d) => d.id != id,
      );
    }
  }
  // This adds emsaver specific fields to our rotating_asset_form
  handleEmsaverFields() {
    const deviceConfig = this.rotating_asset_form.get(
      "device_push_rate_configuration",
    ) as FormGroup;
    const networkConfig = this.rotating_asset_form.get(
      "network_configuration",
    ) as FormGroup;

    const setRequired = (group: FormGroup, fields: string[]) => {
      fields.forEach((f) => {
        group.get(f)?.addValidators(Validators.required);
        group.get(f)?.updateValueAndValidity({ emitEvent: false });
      });
    };

    setRequired(deviceConfig, [
      "summaryFileRefreshRate",
      "fullRefreshFileRate",
      "scanRate",
      "odr_normal",
      "odr_high_res",
      "anomalyAnalysisTime",
      "anomalyCounter",
    ]);

    setRequired(networkConfig, [
      "connectivity_type",
      "wifi_ssid",
      "wifi_password",
    ]);
  }

  updatedRTDs(master_type) {
    let other_config = this.rotating_asset_form.get("other_configurations");

    if (master_type == 1 || master_type == 2) {
      other_config.get("RTD_2").disable({ onlySelf: true });
      other_config.get("RTD_3").disable({ onlySelf: true });
      other_config.get("temp_2_comp").disable({ onlySelf: true });
      other_config.get("temp_3_comp").disable({ onlySelf: true });
    } else {
      other_config.get("RTD_2").enable();
      other_config.get("RTD_3").enable();
      other_config.get("temp_2_comp").enable();
      other_config.get("temp_3_comp").enable();
    }
  }

  /* ---------------------------------------------------------------------HELPER FUNCTIONS------------------------------------------------------- */
  encryptUserId(id: string) {
    return (
      Math.floor(Math.random() * 90 + 10) +
      id +
      Math.floor(Math.random() * 9000 + 1000)
    );
  }

  checkValidInput(value: string) {
    console.log(value);
    return value ? true : false;
  }

  updateAvailableMacAddresses(id: number) {
    this.filtered_devices = this.filtered_devices.filter((d) => d.id != id);
    this.available_devices = this.filtered_devices;
  }

  showSnackBar(message: string) {
    this.snackBar.open(message, "Close", {
      duration: 3000, // auto close after 3 seconds
      horizontalPosition: "right",
      verticalPosition: "top",
    });
  }
}
